import { busy, notBusy } from '../busy/actions.js'
import { handleAddErrors, handleGetErrors, handleUpdateErrors, handleDeleteErrors } from '../error/actions.js'
import { resetFileSearch } from '../files/actions.js'

import api from '../api.js'
import store from '../store.js'
import storage from '../storage.js'

export const PROJECT_CREATE_ACTION = 'PROJECT_CREATE_ACTION'
export const PROJECT_SHOW_ADD_DIALOG_ACTION = 'PROJECT_SHOW_ADD_DIALOG_ACTION'
export const PROJECT_HIDE_ADD_DIALOG_ACTION = 'PROJECT_HIDE_ADD_DIALOG_ACTION'
export const PROJECT_GET_OK_ACTION = 'PROJECT_GET_OK_ACTION'
export const PROJECT_UPDATE_ACTION = 'PROJECT_UPDATE_ACTION'
export const PROJECT_SHOW_EDIT_DIALOG_ACTION = 'PROJECT_SHOW_EDIT_DIALOG_ACTION'
export const PROJECT_HIDE_EDIT_DIALOG_ACTION = 'PROJECT_HIDE_EDIT_DIALOG_ACTION'
export const PROJECT_DELETE_ACTION = 'PROJECT_DELETE_ACTION'
export const PROJECT_SHOW_DELETE_DIALOG_ACTION = 'PROJECT_SHOW_DELETE_DIALOG_ACTION'
export const PROJECT_HIDE_DELETE_DIALOG_ACTION = 'PROJECT_HIDE_DELETE_DIALOG_ACTION'

export const PROJECT_GET_PAGE_ITEMS_ACTION = 'PROJECT_GET_PAGE_ITEMS_ACTION'
export const PROJECT_SET_ORDER_ACTION = 'PROJECT_SET_ORDER_ACTION'
export const PROJECT_SET_LIMIT_ACTION = 'PROJECT_SET_LIMIT_ACTION'
export const PROJECT_SET_OFFSET_ACTION = 'PROJECT_SET_OFFSET_ACTION'
export const PROJECT_SET_SEARCH_ACTION = 'PROJECT_SET_SEARCH_ACTION'
export const PROJECT_RESET_SEARCH_ACTION = 'PROJECT_RESET_SEARCH_ACTION'
export const PROJECT_SET_FILTER_QUERIES_ACTION = 'PROJECT_SET_FILTER_QUERIES_ACTION'
export const PROJECT_SET_SELECTED_ITEM_ID = 'PROJECT_SET_SELECTED_ITEM_ID'

const TAG_FILTER_KEY = 'tags_string'
const OBJECT_TYPE_FILTER_KEY = 'object_type_name_string'
const TAG_FILTER_OP = '~'
const TAG_FILTER_MODE_OR = 'or'
const TAG_FILTER_MODE_AND = 'and'

function filterItemsByTagNames (items, selectedTagNames, matchMode = TAG_FILTER_MODE_OR) {
  if (!Array.isArray(selectedTagNames) || selectedTagNames.length === 0) {
    return items
  }

  const selected = new Set(
    selectedTagNames
      .filter((value) => typeof value === 'string' && value.trim().length > 0)
      .map((value) => value.trim().toLowerCase())
  )

  if (selected.size === 0) {
    return items
  }

  return items.filter((item) => {
    const tagNames = (item?.tags || [])
      .map((tag) => tag?.name)
      .filter((name) => typeof name === 'string' && name.trim().length > 0)
      .map((name) => name.trim().toLowerCase())

    if (matchMode === TAG_FILTER_MODE_AND) {
      return Array.from(selected).every((selectedTag) => tagNames.includes(selectedTag))
    }
    return tagNames.some((name) => selected.has(name))
  })
}

function filterItemsByAnyValues (items, selectedValues, getValue) {
  if (!Array.isArray(selectedValues) || selectedValues.length === 0) {
    return items
  }

  const selected = new Set(
    selectedValues
      .filter((value) => typeof value === 'string' && value.trim().length > 0)
      .map((value) => value.trim().toLowerCase())
  )

  if (selected.size === 0) {
    return items
  }

  return items.filter((item) => {
    const value = getValue(item)
    return typeof value === 'string' && selected.has(value.trim().toLowerCase())
  })
}

export function projectCreateAction (project) {
  return {
    type: PROJECT_CREATE_ACTION,
    project
  }
}

export function projectUpdateAction (project) {
  return {
    type: PROJECT_UPDATE_ACTION,
    project
  }
}

export function projectDeleteAction (project) {
  return {
    type: PROJECT_DELETE_ACTION,
    project
  }
}

export function setSelectedItemId (selectedItemId) {
  return function (dispatch) {
    dispatch({
      type: PROJECT_SET_SELECTED_ITEM_ID,
      selectedItemId
    })
    dispatch(resetFileSearch('project'))
  }
}

/**
 * Add dialog
 */
export function showAddProjectDialog () {
  return {
    type: PROJECT_SHOW_ADD_DIALOG_ACTION
  }
}

export function hideAddProjectDialog () {
  return {
    type: PROJECT_HIDE_ADD_DIALOG_ACTION
  }
}

/**
 * Edit dialog
 */
export function showEditProjectDialog (project) {
  return {
    type: PROJECT_SHOW_EDIT_DIALOG_ACTION,
    project
  }
}

export function hideEditProjectDialog () {
  return {
    type: PROJECT_HIDE_EDIT_DIALOG_ACTION
  }
}

/**
 * Delete dialog
 */
export function showDeleteProjectDialog (project) {
  return {
    type: PROJECT_SHOW_DELETE_DIALOG_ACTION,
    project
  }
}

export function hideDeleteProjectDialog () {
  return {
    type: PROJECT_HIDE_DELETE_DIALOG_ACTION
  }
}

/**
 * CREATE
 */
export function addProject (project) {
  return function (dispatch) {
    dispatch(busy())
    api.addProject(project).then(result => {
      dispatch(notBusy())
    }).catch(handleAddErrors('project', dispatch))
  }
}

/**
 * READ
 */
export function getProjects (params = {}) {
  return function (dispatch) {
    dispatch(busy())
    api.getProjects(params).then(result => {
      dispatch(notBusy())
      dispatch(receivedProjects(result.data.value))
    }).catch(handleGetErrors('project', dispatch))
  }
}

export function receivedProjects (items) {
  return {
    type: PROJECT_GET_OK_ACTION,
    items
  }
}

/**
 * UPDATE
 */
export function updateProject (project) {
  return function (dispatch) {
    dispatch(busy())
    api.updateProject(project).then(() => {
      dispatch(notBusy())
    }).catch(handleUpdateErrors('project', dispatch))
  }
}

/**
 * DELETE
 */
export function deleteProject (project) {
  return function (dispatch) {
    dispatch(busy())
    api.deleteProject(project).then(() => {
      dispatch(notBusy())
      dispatch(getPageItems())
    }).catch(handleDeleteErrors('project', dispatch))
  }
}

export function toggleFavorite (project) {
  return function (dispatch) {
    const favorite = !project.is_favorite
    dispatch(busy())
    api.setProjectFavorite(project.id, favorite).then(() => {
      dispatch(notBusy())
    }).catch(handleUpdateErrors('project', dispatch))
  }
}

/**
 * Get items for a page
 */
export function getPageItems () {
  const state = store.getState()
  const { search, filterQueries, limit, offset, orderBy, order } = state.project

  let query = []
  if (typeof search === 'string' && search.length > 0) {
    const split = search.split(',').map(s => s.trim())
    if (split.length === 1) {
      if (split[0].length > 0) query.push({ key: 'name', value: split[0], op: '~' })
    } else if (split.length === 2) {
      if (split[0].length > 0) query.push({ key: 'name', value: split[0], op: '~' })
      if (split[1].length > 0) query.push({ key: 'collection_name', value: split[1], op: '~' })
    }
  }

  const allFilters = Array.isArray(filterQueries) ? filterQueries : []
  const tagFilters = allFilters.filter((q) => q.key === TAG_FILTER_KEY && q.op === TAG_FILTER_OP)
  const objectTypeFilters = allFilters.filter((q) => q.key === OBJECT_TYPE_FILTER_KEY && q.op === TAG_FILTER_OP)
  const nonTagFilters = allFilters.filter((q) => {
    const isTagFilter = q.key === TAG_FILTER_KEY && q.op === TAG_FILTER_OP
    const isObjectTypeFilter = q.key === OBJECT_TYPE_FILTER_KEY && q.op === TAG_FILTER_OP
    return !isTagFilter && !isObjectTypeFilter
  })
  const selectedTagNames = tagFilters.map((q) => q.value)
  const selectedObjectTypeNames = objectTypeFilters.map((q) => q.value)
  const selectedTagMatchMode = tagFilters.find((q) => q.matchMode === TAG_FILTER_MODE_AND)
    ? TAG_FILTER_MODE_AND
    : TAG_FILTER_MODE_OR
  query = [...query, ...nonTagFilters]

  const sort = orderBy ? `${orderBy}.${order}` : null

  const numericLimit = Number(limit)
  const pageLimit = Number.isFinite(numericLimit) && numericLimit > 0 ? numericLimit : 50
  const numericOffset = Number(offset) || 0

  if (selectedTagNames.length > 0 || selectedObjectTypeNames.length > 0) {
    return function (dispatch) {
      dispatch(busy())
      api.getProjects({
        query,
        sort,
        limit: Math.max(1000, numericOffset + pageLimit),
        offset: 0
      }).then(result => {
        let filteredItems = result.data.value
        filteredItems = filterItemsByTagNames(filteredItems, selectedTagNames, selectedTagMatchMode)
        filteredItems = filterItemsByAnyValues(filteredItems, selectedObjectTypeNames, (item) => item?.object_type_name)
        const pageItems = filteredItems.slice(numericOffset, numericOffset + pageLimit)
        dispatch(notBusy())
        dispatch(receivedProjects(pageItems))
      }).catch(handleGetErrors('project', dispatch))
    }
  }

  return function (dispatch) {
    getProjects({
      query,
      limit,
      offset,
      sort
    })(dispatch)
  }
}

/**
 * Sort order
 */
export function setOrder (orderBy, order) {
  return {
    type: PROJECT_SET_ORDER_ACTION,
    orderBy,
    order
  }
}

/**
 * Set limit for pagination
 */
export function setLimit (limit) {
  return {
    type: PROJECT_SET_LIMIT_ACTION,
    limit
  }
}

/**
 * Set offset for pagination
 */
export function setOffset (offset) {
  return {
    type: PROJECT_SET_OFFSET_ACTION,
    offset
  }
}

/**
 * Set search
 */
export function setSearch (search) {
  return {
    type: PROJECT_SET_SEARCH_ACTION,
    search
  }
}

/**
 * Set filter queries
 */
export function setFilterQueries (filterQueries) {
  storage.putPageSetting('project', { filterQueries })
  return {
    type: PROJECT_SET_FILTER_QUERIES_ACTION,
    filterQueries
  }
}

/**
 * Reset search
 */
export function resetSearch () {
  return {
    type: PROJECT_RESET_SEARCH_ACTION
  }
}
